import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import {
  CanBeChartedProperty,
  resolveReportingProperty,
} from '@principle-theorem/reporting';
import { isCanBeChartedProperty } from '@principle-theorem/reporting/interfaces';
import { firstValueFrom, multiMap } from '@principle-theorem/shared';
import { Observable, ReplaySubject } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { DatapointSelectorComponent } from '../datapoint-selector/datapoint-selector.component';
import { ReportBuilderManageChartBloc } from '../report-builder-results-table/report-builder-add-chart-dialog/report-builder-manage-chart-bloc';

@Component({
  selector: 'pr-report-builder-data-point-selector',
  templateUrl: './report-builder-data-point-selector.component.html',
  styleUrls: ['./report-builder-data-point-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, DatapointSelectorComponent],
})
export class ReportBuilderDataPointSelectorComponent {
  bloc$ = new ReplaySubject<ReportBuilderManageChartBloc>(1);
  measures$: Observable<CanBeChartedProperty[]>;

  @Input() label: string = 'Data Point';

  @ViewChild(DatapointSelectorComponent)
  datapointSelectorComponent: DatapointSelectorComponent;

  @Output()
  dataPointSelected = new EventEmitter<CanBeChartedProperty>();

  @Input()
  set bloc(bloc: ReportBuilderManageChartBloc) {
    if (bloc) {
      this.bloc$.next(bloc);
    }
  }

  constructor() {
    this.measures$ = this.bloc$.pipe(
      switchMap((bloc) => bloc.dataPointSearch.items$),
      multiMap((column) => column.options.map((option) => option.property)),
      map((properties) => properties.flat())
    );
  }

  async handleSelect(datapointId: string): Promise<void> {
    const bloc = await firstValueFrom(this.bloc$);
    const dataSource = bloc
      ? await firstValueFrom(bloc.dataSource$)
      : undefined;
    const property = dataSource
      ? resolveReportingProperty(dataSource.factTable, datapointId)
      : undefined;
    if (!bloc || !property || !isCanBeChartedProperty(property)) {
      return;
    }

    bloc.dataPointSelected(property);
    this.dataPointSelected.emit(property);
    this.datapointSelectorComponent.clear();
  }
}
